import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from 'query';
import {
  type BirthDateType,
  type FileUploadDone,
  formatBirthDateToString,
  handleAxiosError,
  isDateOfBirthValid,
} from 'ui';
import {
  type ApiClientResponseError,
  type ResponseErrorDataObject,
  isDefaultResponseErrorDataObject,
} from 'common/services';
import { updateUserProfile } from '../../services';
import { getProfileQueryKey } from '../keys';

const defaultMutateOnError = (error: ApiClientResponseError<ResponseErrorDataObject>) => {
  handleAxiosError(error, undefined, () => {
    const data = error.response?.data;
    return data && isDefaultResponseErrorDataObject(data) ? data.message : undefined;
  });
};

export const useUpdateUserProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    {
      userData: {
        apt?: string;
        biography?: string;
        birthdate?: Partial<BirthDateType>;
        city?: string;
        company?: string;
        country?: string;
        coverPhoto?: FileUploadDone;
        educationLevel?: string;
        email?: string;
        firstName?: string;
        gender?: string;
        interests?: string[];
        lastName?: string;
        middleName?: string;
        phone?: string;
        profession?: string;
        profilePhoto?: FileUploadDone;
        settings?: {
          translations: { enableTranslations: false } | { enableTranslations: true; language: string };
        };
        state?: string;
        street?: string;
        timeZone?: string;
        zip?: string;
      };
      userId: string;
    }
  >({
    defaultMutateOnError,
    async mutationFn({ userData, userId }) {
      const requestData = {
        apt: userData.apt,
        biography: userData.biography,
        birthdate: isDateOfBirthValid(userData.birthdate) ? formatBirthDateToString(userData.birthdate) : undefined,
        city: userData.city,
        company: userData.company,
        country: userData.country,
        cover_photo: userData.coverPhoto?.response,
        education_level: userData.educationLevel ? { id: userData.educationLevel } : undefined,
        email: userData.email,
        first_name: userData.firstName,
        gender: userData.gender ? { id: userData.gender } : undefined,
        interests: userData.interests?.map((interest) => ({ id: interest })),
        last_name: userData.lastName,
        middle_name: userData.middleName,
        phone: userData.phone,
        profession: userData.profession ? { id: userData.profession } : undefined,
        profile_photo: userData.profilePhoto?.response,
        settings: userData.settings
          ? {
              translations: {
                enable_translations: userData.settings.translations.enableTranslations,
                // TODO: This is a temporary solution to ensure that the language is always set to English if translations are not enabled, we need change BE to accept undefined language as well
                language: userData.settings.translations.enableTranslations
                  ? userData.settings.translations.language
                  : 'en',
              },
            }
          : undefined,
        state: userData.state,
        street: userData.street,
        time_zone: userData.timeZone,
        zip: userData.zip,
      };

      const { data } = await updateUserProfile({ body: { user: requestData }, routeParams: { id: userId } });

      // TODO return model in future
      return data;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: getProfileQueryKey() });
    },
  });
};
